import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useExplorerStore } from '../../stores/explorerStore';

const YMD = /^\d{4}-\d{2}-\d{2}$/;

const isYmd = (value: string | null): value is string => !!value && YMD.test(value);

export interface ExplorerDates {
    startDate: string;
    endDate: string;
    /** True when the page was opened with `?startDate=&endDate=` (e.g. from a chat link). */
    fromUrl: boolean;
    setRange: (start: string, end: string) => void;
    /** `path` with the URL dates appended, so linked detail pages keep the same period. */
    withDates: (path: string) => string;
}

/**
 * Period of an Explorer detail page. `?startDate=&endDate=` wins over the saved Explorer dates
 * and never overwrites them: changing the picker then only updates the URL.
 */
export function useExplorerDates(): ExplorerDates {
    const [searchParams, setSearchParams] = useSearchParams();
    const { getDateFilterMinStartDate, getDateFilterMaxEndDate } = useAuth();
    const minDate = getDateFilterMinStartDate();
    const maxDate = getDateFilterMaxEndDate();
    const storeStart = useExplorerStore((s) => s.startDate);
    const storeEnd = useExplorerStore((s) => s.endDate);
    const setStoreStart = useExplorerStore((s) => s.setStartDate);
    const setStoreEnd = useExplorerStore((s) => s.setEndDate);

    const urlStart = searchParams.get('startDate');
    const urlEnd = searchParams.get('endDate');
    const urlRange = useMemo(() => {
        if (!isYmd(urlStart) || !isYmd(urlEnd) || urlStart > urlEnd) return null;
        const startDate = minDate && urlStart < minDate ? minDate : urlStart;
        const endDate = maxDate && urlEnd > maxDate ? maxDate : urlEnd;
        return startDate <= endDate ? { startDate, endDate } : null;
    }, [urlStart, urlEnd, minDate, maxDate]);

    const setRange = useCallback(
        (start: string, end: string) => {
            if (!urlRange) {
                setStoreStart(start);
                setStoreEnd(end);
                return;
            }
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('startDate', start);
                    next.set('endDate', end);
                    return next;
                },
                { replace: true },
            );
        },
        [urlRange, setSearchParams, setStoreStart, setStoreEnd],
    );

    const withDates = useCallback(
        (path: string) => {
            if (!urlRange) return path;
            const query = new URLSearchParams({ startDate: urlRange.startDate, endDate: urlRange.endDate });
            return `${path}${path.includes('?') ? '&' : '?'}${query.toString()}`;
        },
        [urlRange],
    );

    return {
        startDate: urlRange?.startDate ?? storeStart,
        endDate: urlRange?.endDate ?? storeEnd,
        fromUrl: urlRange !== null,
        setRange,
        withDates,
    };
}
